import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { analyzeGame, type GameReview, type MoveAnalysis } from '@/lib/review-engine';
import type { GameState } from '@/lib/chess-engine';
import { 
  Trophy, 
  Star, 
  TrendingUp, 
  AlertCircle, 
  AlertTriangle, 
  X, 
  Diamond,
  Heart,
  Brain,
  Activity,
  BarChart3
} from 'lucide-react';

interface ChessComGameReviewProps {
  gameStates: GameState[];
  className?: string;
  onAnalysisStart?: () => void;
  onAnalysisFinish?: () => void;
}

const getMoveIcon = (label: MoveAnalysis['label']) => {
  switch (label) {
    case 'brilliant': return <Diamond className="h-3 w-3 text-yellow-400" />;
    case 'great': return <Star className="h-3 w-3 text-green-500" />;
    case 'good': return <TrendingUp className="h-3 w-3 text-blue-500" />;
    case 'inaccuracy': return <AlertCircle className="h-3 w-3 text-orange-500" />;
    case 'mistake': return <AlertTriangle className="h-3 w-3 text-red-500" />;
    case 'blunder': return <X className="h-3 w-3 text-red-700" />;
    default: return <Brain className="h-3 w-3 text-muted-foreground" />;
  }
};

const getMoveColor = (label: MoveAnalysis['label']) => {
  switch (label) {
    case 'brilliant': return 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20';
    case 'great': return 'bg-green-500/10 text-green-500 border-green-500/20';
    case 'good': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    case 'inaccuracy': return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
    case 'mistake': return 'bg-red-500/10 text-red-500 border-red-500/20';
    case 'blunder': return 'bg-red-700/10 text-red-700 border-red-700/20';
    default: return 'bg-muted/50 text-muted-foreground border-muted/30';
  }
};

const formatEvaluation = (evaluation: number): string => {
  if (Math.abs(evaluation) > 1000) {
    return evaluation > 0 ? '+999' : '-999';
  }
  return evaluation > 0 ? `+${(evaluation / 100).toFixed(2)}` : (evaluation / 100).toFixed(2);
};

const getAccuracyColor = (accuracy: number): string => {
  if (accuracy >= 95) return 'text-green-600';
  if (accuracy >= 90) return 'text-green-500';
  if (accuracy >= 80) return 'text-blue-500';
  if (accuracy >= 70) return 'text-yellow-500';
  if (accuracy >= 60) return 'text-orange-500';
  return 'text-red-500';
};

export function ChessComGameReview({ gameStates, className, onAnalysisStart, onAnalysisFinish }: ChessComGameReviewProps) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [review, setReview] = useState<GameReview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyzeGame = async () => {
    if (gameStates.length < 2) {
      setError('Need at least 2 moves to analyze the game');
      return;
    }

    onAnalysisStart?.();
    setIsAnalyzing(true);
    setProgress(0);
    setProgressMessage('Starting analysis...');
    setError(null);
    setReview(null);

    try {
      const controller = new AbortController();
      
      const gameReview = await analyzeGame(
        gameStates,
        (current, total, currentlyAnalyzing) => {
          setProgress((current / total) * 100);
          if (currentlyAnalyzing) {
            setProgressMessage(currentlyAnalyzing);
          }
        },
        controller.signal
      );

      setReview(gameReview);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setError('Analysis was cancelled');
      } else if (err instanceof Error) {
        // Enhanced error handling for different failure types
        if (err.message.includes('timeout')) {
          setError('Analysis timed out - position may be complex. The engine will retry automatically.');
        } else if (err.message.includes('unavailable') || err.message.includes('Circuit breaker')) {
          setError('Engine temporarily unavailable. It will reset automatically - please try again in 30 seconds.');
        } else if (err.message.includes('Worker') || err.message.includes('init')) {
          setError('Engine initialization failed. Please refresh the page and try again.');
        } else {
          setError(`Analysis failed: ${err.message}. The engine uses progressive analysis with automatic retries.`);
        }
      } else {
        setError('Failed to analyze game. Please try again.');
      }
      console.error('Game analysis error:', err);
    } finally {
      setIsAnalyzing(false);
      setProgress(0);
      onAnalysisFinish?.();
    }
  };

  const getMoveStats = (moves: MoveAnalysis[]) => {
    const stats = {
      brilliant: 0,
      great: 0,
      good: 0,
      inaccuracy: 0,
      mistake: 0,
      blunder: 0
    };
    
    moves.forEach(move => {
      if (move.label && move.label in stats) {
        stats[move.label as keyof typeof stats]++;
      }
    });
    
    return stats;
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Game Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!review && !isAnalyzing && (
          <div className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Analyze your game with advanced Stockfish engine
            </p>
            <Button 
              onClick={handleAnalyzeGame}
              disabled={gameStates.length < 2}
              className="w-full"
            >
              <Activity className="h-4 w-4 mr-2" />
              Analyze Game
            </Button>
          </div>
        )}

        {isAnalyzing && (
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{progressMessage}</span>
              <span className="font-medium">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {error && (
          <div className="space-y-3">
            <div className="text-center p-4 bg-destructive/10 text-destructive rounded-lg text-sm">
              <AlertTriangle className="h-4 w-4 mx-auto mb-2" />
              {error}
            </div>
            <Button 
              onClick={handleAnalyzeGame}
              variant="outline"
              size="sm"
              className="w-full"
            >
              Try Again
            </Button>
          </div>
        )}

        {review && (
          <div className="space-y-6">
            {/* Chess.com Style Player Accuracy */}
            <div className="space-y-4">
              <h4 className="font-semibold text-center">Player Performance</h4>
              
              {/* White Player */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white border-2 border-muted rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-black">W</span>
                  </div>
                  <span className="font-medium">White</span>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getAccuracyColor(review.whiteAccuracy)}`}>
                    {review.whiteAccuracy.toFixed(1)}%
                  </div>
                  <div className="text-xs text-muted-foreground">Accuracy</div>
                </div>
              </div>

              {/* Black Player */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-800 border-2 border-muted rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-white">B</span>
                  </div>
                  <span className="font-medium">Black</span>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getAccuracyColor(review.blackAccuracy)}`}>
                    {review.blackAccuracy.toFixed(1)}%
                  </div>
                  <div className="text-xs text-muted-foreground">Accuracy</div>
                </div>
              </div>
            </div>

            {/* Move Classification Stats */}
            <div className="space-y-3">
              <h4 className="font-semibold">Move Classification</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(getMoveStats(review.moves)).map(([label, count]) => (
                  <div key={label} className="flex items-center justify-between p-2 bg-muted/20 rounded">
                    <div className="flex items-center gap-2">
                      {getMoveIcon(label as MoveAnalysis['label'])}
                      <span className="capitalize">{label}</span>
                    </div>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Move by Move Analysis */}
            <div className="space-y-3">
              <h4 className="font-semibold">Move Analysis</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {review.moves.map((move, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted/20 rounded-lg hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs bg-muted px-2 py-1 rounded">
                        {Math.floor(index / 2) + 1}{index % 2 === 0 ? '.' : '...'}
                      </span>
                      {move.label && (
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${getMoveColor(move.label)}`}
                        >
                          {getMoveIcon(move.label)}
                          <span className="ml-1 capitalize">{move.label}</span>
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="font-mono text-muted-foreground">
                        {formatEvaluation(move.evaluation)}
                      </span>
                      <span className={`font-semibold ${getAccuracyColor(move.accuracy)}`}>
                        {move.accuracy.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Re-analyze Button */}
            <Button 
              onClick={handleAnalyzeGame}
              variant="outline"
              className="w-full"
              size="sm"
            >
              Re-analyze Game
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}